/**
 * Painel DIREITO do /vault: cofre selecionado com toolbar (busca local,
 * '+ Pasta', '+ Senha' — ocultos no nível Leitura), navegação de pastas por
 * níveis (client-side, a partir da lista flat) com breadcrumb e linha '..',
 * e a tabela de senhas (Nome | Usuário | URL | IP | Cliente | Atualizado |
 * ações no hover: revelar, copiar senha, editar, excluir).
 *
 * Segurança: nenhum segredo aparece aqui — 'copiar senha' faz o reveal em
 * background e manda direto pro clipboard; 'revelar' abre o RevealDialog.
 */
import { Fragment, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  ChevronRight,
  Copy,
  CornerLeftUp,
  ExternalLink,
  Eye,
  Folder,
  FolderPlus,
  History,
  Pencil,
  Plus,
  Search,
  Trash2,
} from 'lucide-react';
import type { VaultEntry, VaultFolder } from '@erp/shared';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { EmptyState, Skeleton } from '@/components/ui/misc';
import { cn } from '@/lib/utils';
import { errorMessage, vaultTreeOptions } from './vault-queries';
import { useRevealVaultEntry } from './vault-mutations';
import { formatDateBR, LevelBadge, vaultErrorMessage } from './vault-format';
import { copyText } from './vault-clipboard';
import { useVaultToast } from './vault-toast';
import {
  DeleteVaultEntryDialog,
  DeleteVaultFolderDialog,
  VaultFolderDialog,
  VaultLogsDialog,
} from './vault-dialogs';
import { VaultEntryDrawer } from './entry-drawer';
import { RevealDialog } from './reveal-dialog';

type PaneDialog =
  | { kind: 'new-folder' }
  | { kind: 'rename-folder'; folder: VaultFolder }
  | { kind: 'delete-folder'; folder: VaultFolder }
  | { kind: 'delete-entry'; entry: VaultEntry }
  | { kind: 'logs' };

type DrawerState = { mode: 'new' } | { mode: 'edit'; entry: VaultEntry };

const COLS = 7;

/** Botão-ícone de célula/ação: aparece no hover da linha (group). */
function RowIconButton({
  label,
  destructive,
  onClick,
  children,
}: {
  label: string;
  destructive?: boolean;
  onClick: (e: React.MouseEvent) => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      title={label}
      aria-label={label}
      onClick={onClick}
      className={cn(
        'flex size-6 shrink-0 items-center justify-center rounded-md text-muted-foreground opacity-0 transition-[opacity,color,background-color] outline-none group-hover:opacity-100 hover:bg-accent focus-visible:opacity-100 focus-visible:ring-2 focus-visible:ring-ring/50',
        destructive ? 'hover:text-destructive' : 'hover:text-primary'
      )}
    >
      {children}
    </button>
  );
}

export function VaultDetailPane({ vaultId }: { vaultId: number | null }) {
  const toast = useVaultToast();
  const treeQ = useQuery({ ...vaultTreeOptions(vaultId ?? 0), enabled: vaultId !== null });
  const reveal = useRevealVaultEntry();

  const [folderId, setFolderId] = useState<number | null>(null);
  const [search, setSearch] = useState('');
  const [drawer, setDrawer] = useState<DrawerState | null>(null);
  const [revealEntry, setRevealEntry] = useState<VaultEntry | null>(null);
  const [dialog, setDialog] = useState<PaneDialog | null>(null);

  const folders = useMemo(() => treeQ.data?.folders ?? [], [treeQ.data]);
  const entries = useMemo(() => treeQ.data?.entries ?? [], [treeQ.data]);

  const term = search.trim().toLowerCase();
  const searching = term !== '';

  const currentFolder = folderId !== null ? folders.find((f) => f.id === folderId) : undefined;

  /** Cadeia de pastas até a atual (breadcrumb), a partir da lista flat. */
  const chain = useMemo(() => {
    const byId = new Map(folders.map((f) => [f.id, f]));
    const list: VaultFolder[] = [];
    let cursor = currentFolder;
    while (cursor !== undefined && list.length <= folders.length) {
      list.unshift(cursor);
      cursor = cursor.parentId !== null ? byId.get(cursor.parentId) : undefined;
    }
    return list;
  }, [folders, currentFolder]);

  const childFolders = useMemo(
    () =>
      folders
        .filter((f) => f.parentId === folderId)
        .sort((a, b) => a.name.localeCompare(b.name, 'pt-BR')),
    [folders, folderId]
  );

  const visibleEntries = useMemo(() => {
    const matches = (e: VaultEntry) =>
      [e.name, e.description, e.username, e.url, e.ip, e.clientName].some(
        (v) => v !== null && v.toLowerCase().includes(term)
      );
    const base = searching ? entries.filter(matches) : entries.filter((e) => e.folderId === folderId);
    return base.slice().sort((a, b) => a.name.localeCompare(b.name, 'pt-BR'));
  }, [entries, folderId, searching, term]);

  if (vaultId === null) {
    return (
      <section className="flex min-w-0 flex-1 items-center justify-center rounded-lg border bg-card shadow-xs">
        <EmptyState message="Selecione um cofre à esquerda — ou crie o primeiro" />
      </section>
    );
  }

  if (treeQ.isPending) {
    return (
      <section aria-busy className="flex min-w-0 flex-1 flex-col rounded-lg border bg-card shadow-xs">
        <div className="space-y-3 border-b p-3">
          <Skeleton className="h-5 w-48" />
          <Skeleton className="h-8 w-full" />
        </div>
        <div className="space-y-3 p-4">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-6 w-full" />
          ))}
        </div>
      </section>
    );
  }

  if (treeQ.isError) {
    return (
      <section className="flex min-w-0 flex-1 items-center justify-center rounded-lg border bg-card shadow-xs">
        <EmptyState message={`Erro ao carregar o cofre: ${errorMessage(treeQ.error)}`} />
      </section>
    );
  }

  const { vault } = treeQ.data;
  const canWrite = vault.myLevel === 'write';

  /** Copiar valor em claro (usuário/URL — nunca a senha por aqui). */
  const copyPlain = async (value: string, message: string) => {
    const ok = await copyText(value);
    toast(ok ? message : 'Não foi possível copiar');
  };

  /** Copiar SENHA: reveal em background → clipboard direto, sem renderizar. */
  const copySecret = async (entry: VaultEntry) => {
    try {
      const secret = await reveal.mutateAsync(entry.id);
      // Não reter o segredo no estado interno da mutation.
      reveal.reset();
      const ok = await copyText(secret);
      toast(ok ? 'Senha copiada' : 'Não foi possível copiar');
    } catch (e) {
      toast(vaultErrorMessage(e));
    }
  };

  return (
    <section className="flex min-w-0 flex-1 flex-col overflow-hidden rounded-lg border bg-card shadow-xs">
      {/* Header do cofre */}
      <div className="flex shrink-0 flex-wrap items-center gap-2 border-b p-3">
        <h2 className="min-w-0 truncate text-[15px] font-semibold">{vault.name}</h2>
        <LevelBadge level={vault.myLevel} />
        {canWrite ? (
          <Button
            variant="outline"
            size="sm"
            className="ml-auto"
            onClick={() => setDialog({ kind: 'logs' })}
          >
            <History className="size-4" />
            Histórico
          </Button>
        ) : null}
      </div>

      {/* Toolbar: busca + ações de escrita */}
      <div className="flex shrink-0 items-center gap-2 border-b p-3">
        <div className="relative min-w-0 flex-1 sm:max-w-72">
          <Search className="pointer-events-none absolute top-1/2 left-2 size-3.5 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar neste cofre…"
            aria-label="Buscar senhas neste cofre"
            className="h-8 pl-7 text-sm"
          />
        </div>
        {canWrite ? (
          <div className="ml-auto flex shrink-0 items-center gap-2">
            <Button variant="ghost" size="sm" onClick={() => setDialog({ kind: 'new-folder' })}>
              <FolderPlus className="size-4" />
              Pasta
            </Button>
            <Button size="sm" onClick={() => setDrawer({ mode: 'new' })}>
              <Plus className="size-4" strokeWidth={2.5} />
              Senha
            </Button>
          </div>
        ) : null}
      </div>

      {/* Breadcrumb da navegação (fora da busca) */}
      <div className="flex shrink-0 flex-wrap items-center gap-1 border-b px-4 py-2 text-[13px]">
        {searching ? (
          <span className="text-muted-foreground">
            Resultados em todo o cofre — limpe a busca para navegar por pastas
          </span>
        ) : (
          <>
            <button
              type="button"
              onClick={() => setFolderId(null)}
              className={cn(
                'transition-colors',
                folderId === null
                  ? 'font-medium text-foreground'
                  : 'text-primary hover:text-primary-pressed'
              )}
            >
              {vault.name}
            </button>
            {chain.map((f, i) => (
              <Fragment key={f.id}>
                <ChevronRight className="size-3.5 text-muted-foreground" aria-hidden />
                {i === chain.length - 1 ? (
                  <span className="font-medium">{f.name}</span>
                ) : (
                  <button
                    type="button"
                    onClick={() => setFolderId(f.id)}
                    className="text-primary transition-colors hover:text-primary-pressed"
                  >
                    {f.name}
                  </button>
                )}
              </Fragment>
            ))}
          </>
        )}
      </div>

      {/* Pastas + senhas */}
      <div className="min-h-0 flex-1 overflow-y-auto">
        {!searching && childFolders.length === 0 && visibleEntries.length === 0 && folderId === null ? (
          <EmptyState
            message={
              canWrite
                ? 'Cofre vazio — crie uma pasta ou adicione a primeira senha'
                : 'Cofre vazio'
            }
          />
        ) : searching && visibleEntries.length === 0 ? (
          <EmptyState message="Nenhuma senha encontrada para a busca" />
        ) : (
          <table className="w-full text-sm">
            <thead className="sticky top-0 z-10">
              <tr className="bg-muted text-left text-[13px]">
                <th className="h-9 px-4 font-semibold">Nome</th>
                <th className="h-9 px-4 font-semibold">Usuário</th>
                <th className="h-9 px-4 font-semibold">URL</th>
                <th className="h-9 px-4 font-semibold">IP</th>
                <th className="h-9 px-4 font-semibold">Cliente</th>
                <th className="h-9 px-4 font-semibold">Atualizado</th>
                <th className="h-9 px-4 text-right font-semibold">
                  <span className="sr-only">Ações</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {/* '..' voltar */}
              {!searching && folderId !== null ? (
                <tr
                  onClick={() => setFolderId(currentFolder?.parentId ?? null)}
                  className="h-10 cursor-pointer border-b border-divider transition-colors hover:bg-accent/60"
                >
                  <td className="px-4" colSpan={COLS}>
                    <span className="flex items-center gap-2 text-muted-foreground">
                      <CornerLeftUp className="size-4" aria-hidden />
                      ..
                    </span>
                  </td>
                </tr>
              ) : null}

              {/* Pastas do nível atual */}
              {!searching
                ? childFolders.map((folder) => (
                    <tr
                      key={`f-${folder.id}`}
                      onClick={() => setFolderId(folder.id)}
                      className="group h-10 cursor-pointer border-b border-divider transition-colors hover:bg-accent/60"
                    >
                      <td className="px-4" colSpan={COLS - 1}>
                        <span className="flex items-center gap-2 font-medium">
                          <Folder
                            className="size-4 shrink-0 fill-warning/25 text-warning"
                            aria-hidden
                          />
                          <span className="truncate">{folder.name}</span>
                        </span>
                      </td>
                      <td className="px-4">
                        {canWrite ? (
                          <div className="flex justify-end gap-1">
                            <RowIconButton
                              label={`Renomear pasta ${folder.name}`}
                              onClick={(e) => {
                                e.stopPropagation();
                                setDialog({ kind: 'rename-folder', folder });
                              }}
                            >
                              <Pencil className="size-3.5" />
                            </RowIconButton>
                            <RowIconButton
                              destructive
                              label={`Excluir pasta ${folder.name}`}
                              onClick={(e) => {
                                e.stopPropagation();
                                setDialog({ kind: 'delete-folder', folder });
                              }}
                            >
                              <Trash2 className="size-3.5" />
                            </RowIconButton>
                          </div>
                        ) : null}
                      </td>
                    </tr>
                  ))
                : null}

              {/* Senhas */}
              {visibleEntries.map((entry) => (
                <tr
                  key={entry.id}
                  className="group h-10 border-b border-divider transition-colors last:border-0 hover:bg-accent/60"
                >
                  <td className="max-w-56 px-4 py-1.5">
                    <div className="truncate font-medium" title={entry.name}>
                      {entry.name}
                    </div>
                    {entry.description ? (
                      <div
                        className="truncate text-xs text-muted-foreground"
                        title={entry.description}
                      >
                        {entry.description}
                      </div>
                    ) : null}
                  </td>
                  <td className="px-4 whitespace-nowrap">
                    {entry.username !== null && entry.username !== '' ? (
                      <span className="flex items-center gap-1">
                        <span className="max-w-36 truncate">{entry.username}</span>
                        {/* usuário é texto claro na listagem — cópia direta, sem reveal */}
                        <RowIconButton
                          label="Copiar usuário"
                          onClick={() => void copyPlain(entry.username ?? '', 'Usuário copiado')}
                        >
                          <Copy className="size-3.5" />
                        </RowIconButton>
                      </span>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </td>
                  <td className="px-4 whitespace-nowrap">
                    {entry.url !== null && entry.url !== '' ? (
                      <span className="flex items-center gap-1">
                        <span
                          className="max-w-40 truncate text-muted-foreground"
                          title={entry.url}
                        >
                          {entry.url.replace(/^https?:\/\//, '')}
                        </span>
                        <RowIconButton
                          label="Copiar URL"
                          onClick={() => void copyPlain(entry.url ?? '', 'URL copiada')}
                        >
                          <Copy className="size-3.5" />
                        </RowIconButton>
                        <a
                          href={entry.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          title="Abrir em nova aba"
                          aria-label={`Abrir ${entry.url} em nova aba`}
                          className="flex size-6 shrink-0 items-center justify-center rounded-md text-muted-foreground opacity-0 transition-[opacity,color,background-color] outline-none group-hover:opacity-100 hover:bg-accent hover:text-primary focus-visible:opacity-100 focus-visible:ring-2 focus-visible:ring-ring/50"
                        >
                          <ExternalLink className="size-3.5" />
                        </a>
                      </span>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </td>
                  <td className="px-4 whitespace-nowrap text-muted-foreground tabular-nums">
                    {entry.ip !== null && entry.ip !== '' ? entry.ip : '—'}
                  </td>
                  <td className="max-w-36 truncate px-4" title={entry.clientName ?? undefined}>
                    {entry.clientName ?? <span className="text-muted-foreground">—</span>}
                  </td>
                  <td className="px-4 whitespace-nowrap text-muted-foreground tabular-nums">
                    {formatDateBR(entry.updatedAt)}
                  </td>
                  <td className="px-4">
                    <div className="flex justify-end gap-1">
                      <RowIconButton
                        label={`Revelar senha ${entry.name}`}
                        onClick={() => setRevealEntry(entry)}
                      >
                        <Eye className="size-3.5" />
                      </RowIconButton>
                      <RowIconButton
                        label={`Copiar senha ${entry.name}`}
                        onClick={() => void copySecret(entry)}
                      >
                        <Copy className="size-3.5" />
                      </RowIconButton>
                      {canWrite ? (
                        <>
                          <RowIconButton
                            label={`Editar ${entry.name}`}
                            onClick={() => setDrawer({ mode: 'edit', entry })}
                          >
                            <Pencil className="size-3.5" />
                          </RowIconButton>
                          <RowIconButton
                            destructive
                            label={`Excluir ${entry.name}`}
                            onClick={() => setDialog({ kind: 'delete-entry', entry })}
                          >
                            <Trash2 className="size-3.5" />
                          </RowIconButton>
                        </>
                      ) : null}
                    </div>
                  </td>
                </tr>
              ))}

              {/* Pasta atual vazia (sem subpastas e sem senhas) */}
              {!searching &&
              folderId !== null &&
              childFolders.length === 0 &&
              visibleEntries.length === 0 ? (
                <tr>
                  <td colSpan={COLS}>
                    <EmptyState className="h-28" message="Pasta vazia" />
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        )}
      </div>

      {/* Dialogs / drawer / reveal */}
      {drawer !== null ? (
        <VaultEntryDrawer
          vaultId={vault.id}
          folders={folders}
          defaultFolderId={searching ? null : folderId}
          initial={drawer.mode === 'edit' ? drawer.entry : undefined}
          onClose={() => setDrawer(null)}
        />
      ) : null}
      {revealEntry !== null ? (
        <RevealDialog entry={revealEntry} onClose={() => setRevealEntry(null)} />
      ) : null}
      {dialog?.kind === 'new-folder' ? (
        <VaultFolderDialog
          vaultId={vault.id}
          parentId={searching ? null : folderId}
          onClose={() => setDialog(null)}
        />
      ) : null}
      {dialog?.kind === 'rename-folder' ? (
        <VaultFolderDialog
          vaultId={vault.id}
          parentId={dialog.folder.parentId}
          folder={dialog.folder}
          onClose={() => setDialog(null)}
        />
      ) : null}
      {dialog?.kind === 'delete-folder' ? (
        <DeleteVaultFolderDialog
          folder={dialog.folder}
          onDeleted={() => {
            if (folderId === dialog.folder.id) setFolderId(dialog.folder.parentId);
          }}
          onClose={() => setDialog(null)}
        />
      ) : null}
      {dialog?.kind === 'delete-entry' ? (
        <DeleteVaultEntryDialog entry={dialog.entry} onClose={() => setDialog(null)} />
      ) : null}
      {dialog?.kind === 'logs' ? (
        <VaultLogsDialog vault={vault} onClose={() => setDialog(null)} />
      ) : null}
    </section>
  );
}
